// src/components/documents/EstimateEditor.js — doced-8f3a61c2 (Document editor, Stage 4: the estimate page)
//
// The full-page estimate editor (shown only when the company switch documentPagesEnabled.estimates is
// on — Spatchy Test Company only). Left: title, customer, dates, salesperson, line items, "Message to
// the customer" (`description`, shown on the portal), "Internal notes" (`notes` — the pop-up's
// "Internal notes" box, once labelled "Notes / Terms"; staff only), Notes & media. Right (stays in view): status, totals, deposit, Save.
// "Terms" (`terms`, Stage 5e): customer-facing, printed on the PDF and the portal; a new estimate starts with
// the company default from Settings › Documents.
//
// THE SAVE IS THE POP-UP'S. Founder rule (2026-10-25): an estimate saved here is exactly what today's
// pop-up (QuoteFormModal.js handleSave) would have written from the same editor state, so either screen
// can open and save the other's estimates, and the switch can go back off with nothing lost:
//   the same checks in the same order → the optional-deposit confirm → "save to catalog" items made →
//   estimateSave.buildEstimateWrite (+ buildEstimateCreate for a new one) → the staged note committed.
// Two deliberate differences, both from the plan:
//   • totals come from the totals engine (per cent, once per tax part) — the pop-up doesn't round;
//   • the write happens INSIDE a transaction that refuses to overwrite someone else's change
//     (estimateConflict.saveWithConflictCheck): the person sees what changed and can reload, or save
//     theirs over it on purpose (never over a status or deposit change).
// The page never writes a per-line taxSnapshot.
//
// taxpick4-54573069 — Stage S4 (tax codes on every line): when the company's Settings › Taxes switch is ON
// (estimateLineTax.pageTaxMode; today the test company only) the page gets one tax picker per line (its
// Settings › Taxes codes, or its QuickBooks codes), "Set tax for all lines", the catalog item's code on new
// lines, the customer's exemption (banner + totals + the per-document switch-off), the field-added marker,
// and its save stores what the server's engine would (lines stamped with a code + kept copy, taxParts,
// totalsEngineVersion, taxExemptionApplied). Taxes OFF (every real company): every line of this file runs
// exactly as before S4 — the `taxOn` branches are skipped.
//
// While nobody is typing, a change saved elsewhere (the pop-up, the phone, the customer, the number
// arriving) simply appears. While there are unsaved edits, the page says someone else changed it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db, functions } from '../../firebase/config';
import { doc, getDoc, addDoc, updateDoc, collection, query, orderBy, onSnapshot, deleteDoc, serverTimestamp, runTransaction, deleteField } from 'firebase/firestore'; // taxlines2-3e0b16a4 — deleteField drops stale tax parts
import { dropStaleTaxParts } from '../../utils/taxPartsOnSave'; // taxlines2-3e0b16a4
import { httpsCallable } from 'firebase/functions';
import { FileText, X, ChevronDown, ChevronRight, AlertTriangle, ListPlus, Send } from 'lucide-react';
import { statusAllowsSend } from '../../utils/estimateSend'; // doced-8f3a61c2 — Stage 5f
import { canUpdateCustomerRecord, withEstimateEmail, updateCustomerRecordEmail, plannedChanges, estimateEmailOf, RECORD_UPDATED } from '../../utils/estimateEmailChange'; // doced-8f3a61c2 — Stage 5f-2
import useOrgNotes from '../../hooks/useOrgNotes';
import useUnsavedGuard from '../../hooks/useUnsavedGuard';
import NoteComposer from '../crm/NoteComposer';
import NoteCard from '../crm/NoteCard';
import ConfirmationModal from '../common/ConfirmationModal';
import CustomerFormModal from '../crm/CustomerFormModal';
import DocumentCustomerCard from './DocumentCustomerCard';
import DocumentLineItems from './DocumentLineItems';
import DocumentTotalsPanel from './DocumentTotalsPanel';
import DocumentProfitCard from './DocumentProfitCard'; // doced-8f3a61c2 — Stage 5g
import CatalogQuickAdd from './CatalogQuickAdd';
import CatalogPickerPanel from './CatalogPickerPanel';
import { useCatalogFolders } from './useCatalogSearch';
import { FIELD, SELECT, SELECT_STYLE } from './fieldStyles';
import * as lineIds from '../../utils/lineIds';
import { parseLocalDate, formatLocalDate, formatCurrency, stripFirestoreSentinels } from '../../utils/formatters';
import {
    generateItemId, DOC_TYPES, resolveDocTerminology, resolveCreateSalesPersonId, resolveQuoteValidityDays,
    resolveDepositAmount, normalizeStatus, QUOTE_STATUSES,
    DEPOSIT_AMOUNT_NEEDED_MESSAGE, // depdraft-3c8e5a14
} from '../../utils/quoteSchema';
import { canOrgTakeOnlinePayments } from '../../utils/orgPayments';
import { HIGH_LEVEL_ROLES } from '../../utils/permissions';
import { canUseCatalog } from '../../utils/catalogUi';
import { newEstimateLine, appendPickedEstimateLines, isUntouchedEstimateLine } from '../../utils/catalogPickLines';
import { buildEstimateWrite, buildEstimateCreate, linesNeedingCatalogItem, catalogItemForLine, linesAfterCatalogStep } from '../../utils/estimateSave';
import { fingerprintOf, changedFields, describeChanges, saveWithConflictCheck, EstimateConflictError } from '../../utils/estimateConflict';
import {
    initialQuoteData, autoTitle, effectiveDocType, seedDeposit, isApprovalBlockedByDeposit, optionalDepositOnApproval,
    findSaveProblem, estimateTaxContext, estimateTotals, depositAmountMissing,
    termsLockedFor, TERMS_LOCKED_NOTE, // secfix-7b3e9d42
} from '../../utils/estimateEditor';import { termsProblem } from '../../utils/documentSettings'; // doced-8f3a61c2 — Stage 5e
import { chooseCustomer, chooseServiceLocation, chooseBillTo, chooseThirdParty, initialServiceLocation, initialBillToTouched } from '../../utils/documentCustomer';
import { DeleteQuoteButton } from '../crm/QuoteDeleteControls'; // estdel-f9ac8ba8
import TaxExemptionBanner from '../common/TaxExemptionBanner'; // taxpick4-54573069
import { useTaxCodesDoc } from '../../utils/taxExemptionUi'; // taxpick4-54573069
import { LINE_SELECT } from './fieldStyles'; // taxpick4-54573069
import {
    TAX_MODES, pageTaxMode, onModeContext, onModeTotals, applyOnModeSave, canPickLineTax, isOfficeTaxRole,
    setAllChoices, setAllLines, appendPicksWithTax, withItemSalesTax, onModeRowLabel, taxChangeNote, fieldTaxCheckCount,
    SET_ALL_DONE, TAX_SETTINGS_LOADING,
} from '../../utils/estimateLineTax'; // taxpick4-54573069

const IDS = { generateItemId, newLine: lineIds.newLine, newLineId: lineIds.newLineId };
const LEAVE_MESSAGE = 'This estimate has changes that aren’t saved. Leave without saving?';

function freshDays(companySettings) {
    const today = formatLocalDate();
    const exp = new Date();
    exp.setDate(exp.getDate() + resolveQuoteValidityDays(companySettings?.quoteDefaults));
    return { today, defaultExpiry: formatLocalDate(exp) };
}

/** Everything the editor holds, from an estimate (stored, prefill, or null for blank). */
function editorStateFrom(source, companySettings) {
    const { today, defaultExpiry } = freshDays(companySettings);
    return {
        source,
        qd: initialQuoteData(source, { today, defaultExpiry, newLine: () => newEstimateLine(IDS), generateItemId, companySettings }),
        serviceLocation: initialServiceLocation(source),
        pendingThirdParty: false,
        billToTouched: initialBillToTouched(source),
        deposit: seedDeposit(source, companySettings),
        salesTouched: false,
    };
}

export default function EstimateEditor({
    orgId, user, currentUserProfile, companySettings, customers, users,
    original, prefill, newRef, live, listLabel,
    onSaved, onBackToList, onBackToReadView, onClosingSave,
    onReviewAndSend, // doced-8f3a61c2 — Stage 5f: (quoteId) → the Review & send page
}) {
    const isNew = !original?.id;
    const quoteId = isNew ? newRef?.id : original.id;
    const role = currentUserProfile?.role;
    const isHighLevel = HIGH_LEVEL_ROLES.includes(role);
    const currency = companySettings?.currency || 'USD';

    const [st, setSt] = useState(() => editorStateFrom(original || prefill || null, companySettings));
    const { source, qd, serviceLocation, pendingThirdParty, deposit, salesTouched } = st;
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [conflict, setConflict] = useState(null); // { message, reloadOnly, fields }
    const [outsideChange, setOutsideChange] = useState(null); // fields changed elsewhere while editing
    const [depositConfirm, setDepositConfirm] = useState(null);
    const [savedOnce, setSavedOnce] = useState(false);

    const baselineRef = useRef(fingerprintOf(original || {}));
    const dirtyRef = useRef(false);
    const savingRef = useRef(false);
    const editSeqRef = useRef(0);
    const liveRef = useRef(live);
    liveRef.current = live;
    dirtyRef.current = dirty;

    const touch = () => { editSeqRef.current += 1; setDirty(true); setError(''); };
    const patch = (p) => { setSt((s) => ({ ...s, ...(typeof p === 'function' ? p(s) : p) })); touch(); };
    const setQd = (fn) => patch((s) => ({ qd: typeof fn === 'function' ? fn(s.qd) : fn }));

    const reinit = (data) => {
        setSt(editorStateFrom(data, companySettings));
        baselineRef.current = fingerprintOf(data || {});
        setDirty(false); setOutsideChange(null); setConflict(null); setError(''); setShowErrors(false);
    };

    // ── someone else's change ────────────────────────────────────────────────
    const liveKey = live ? `${JSON.stringify(fingerprintOf(live))}|${live.quoteNumber || ''}` : '';
    useEffect(() => {
        // While saving, the change arriving is (almost always) our own; the save re-reads the estimate
        // when it finishes and starts again from that, so nothing is decided here meanwhile.
        if (isNew || !live || savingRef.current) return;
        if (!dirtyRef.current) { reinit(live); return; }
        const fields = changedFields(baselineRef.current, fingerprintOf(live));
        setOutsideChange(fields.length ? fields : null);
    }, [liveKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── tax codes (QuickBooks companies only) ────────────────────────────────
    const qbConnected = companySettings?.quickbooksConnected === true;
    const [tax, setTax] = useState({ options: [], loaded: !qbConnected, error: false });
    const [taxNonce, setTaxNonce] = useState(0);
    useEffect(() => {
        if (!qbConnected) { setTax({ options: [], loaded: true, error: false }); return undefined; }
        let cancelled = false;
        setTax((t) => ({ ...t, loaded: false, error: false }));
        httpsCallable(functions, 'getQuickBooksMappingOptions')()
            .then((res) => { if (!cancelled) setTax({ options: res.data?.taxCodes || [], loaded: true, error: false }); })
            .catch((e) => { console.error('doced: tax codes failed to load', e); if (!cancelled) setTax({ options: [], loaded: false, error: true }); });
        return () => { cancelled = true; };
    }, [qbConnected, taxNonce]);
    const taxCtx = { companySettings, taxOptions: tax.options, taxLoaded: tax.loaded };
    const engineCtx = useMemo(() => estimateTaxContext(taxCtx), [companySettings, tax]); // eslint-disable-line react-hooks/exhaustive-deps
    const offTotals = useMemo(() => estimateTotals(qd.lineItems, engineCtx), [qd.lineItems, engineCtx]);

    // taxpick4-54573069 — Settings › Taxes ON only (taxOn). OFF: onCtx is null and nothing below changes.
    const taxCodesDoc = useTaxCodesDoc(orgId);
    const taxMode = pageTaxMode({ orgId, taxCodesDoc, qbConnected });
    const taxOn = taxMode !== TAX_MODES.OFF;
    const taxDocLoading = taxOn && taxCodesDoc === undefined;
    const taxCustomer = (customers || []).find((c) => c.id === qd.customerId) || null;
    const onCtx = useMemo(() => (taxOn
        ? onModeContext({ mode: taxMode, orgId, companySettings, taxOptions: tax.options, taxLoaded: tax.loaded, taxCodesDoc, customer: taxCustomer })
        : null), [taxOn, taxMode, orgId, companySettings, tax, taxCodesDoc, taxCustomer]);
    const onResult = useMemo(() => (onCtx ? onModeTotals(qd.lineItems, onCtx, source) : null), [qd.lineItems, onCtx, source]);
    const totals = onResult ? onResult.totals : offTotals;

    const taxProblem = totals.unresolved
        ? (taxMode === TAX_MODES.NAMED
            ? 'A line’s tax code couldn’t be found in Settings › Taxes, so it is taxed at your default code for now.'
            : (tax.error
                ? 'Your QuickBooks tax codes couldn’t be loaded, so lines with a QuickBooks code are taxed at your company rate for now.'
                : 'Loading your QuickBooks tax codes… until then, lines with a QuickBooks code are taxed at your company rate.'))
        : (taxDocLoading ? TAX_SETTINGS_LOADING : null);

    // ── words ────────────────────────────────────────────────────────────────
    const docType = isNew ? effectiveDocType(qd, companySettings) : source?.docType;
    const term = resolveDocTerminology(docType);
    const docWordLower = term.word.toLowerCase();

    // ── deposit ──────────────────────────────────────────────────────────────
    const depositLocked = !!qd.depositStatus;
    const termsLocked = termsLockedFor(source); // secfix-7b3e9d42 — the SAVED estimate is approved: Terms can't change
    const approvalBlocked = isApprovalBlockedByDeposit(source, companySettings);
    const optionalDep = optionalDepositOnApproval({ depositLocked, deposit, editingQuoteStatus: source?.status, approvedTotal: qd.approvedTotal, total: totals.total });
    const depositPreview = resolveDepositAmount(deposit.type, deposit.value, totals.total);

    // taxpick4-54573069 — Taxes on only: an older estimate whose stored lines, totalled the way a save now
    // would, don't give its stored tax (answer 1), the pickers, "Set tax for all lines", the field markers.
    const storedLinesTax = useMemo(() => (onCtx && source?.id ? onModeTotals(source.lineItems || [], onCtx, source).totals.taxAmount : null), [onCtx, source]);
    const taxNote = onCtx ? taxChangeNote({ source, storedLinesTax, newTax: totals.taxAmount, currency, docWordLower }) : null;
    const [taxConfirm, setTaxConfirm] = useState(null);
    const taxPick = onCtx ? { ctx: onCtx, canPick: canPickLineTax(role), showFieldMarker: isOfficeTaxRole(role) } : undefined;
    const [setAllDoneFor, setSetAllDoneFor] = useState(null);
    const fieldMarks = onCtx && isOfficeTaxRole(role) ? fieldTaxCheckCount(qd.lineItems) : 0;

    // ── customer ─────────────────────────────────────────────────────────────
    const customerLocked = !!source?.customer?.id;
    const selectedCustomer = (customers || []).find((c) => c.id === qd.customerId) || null;
    const [newCustomerOpen, setNewCustomerOpen] = useState(false);

    // ── salesperson ──────────────────────────────────────────────────────────
    const salesPersonId = isNew && !salesTouched ? resolveCreateSalesPersonId(selectedCustomer, currentUserProfile?.id) : (qd.salesPersonId || '');
    const salesName = (id) => (users || []).find((u) => u.id === id)?.name || (id === currentUserProfile?.id ? (currentUserProfile?.name || 'You') : '');
    const salesOptions = useMemo(() => {
        const list = (users || []).map((u) => ({ id: u.id, name: u.name || u.email || 'Unnamed' })).sort((a, b) => a.name.localeCompare(b.name));
        if (salesPersonId && !list.some((u) => u.id === salesPersonId)) list.push({ id: salesPersonId, name: salesName(salesPersonId) || 'Someone no longer active' });
        return list;
    }, [users, salesPersonId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── templates (new estimates only, as in the pop-up) ─────────────────────
    const [templates, setTemplates] = useState([]);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    useEffect(() => {
        if (!isNew || !orgId) return undefined;
        return onSnapshot(query(collection(db, `organizations/${orgId}/jobTemplates`), orderBy('name')),
            (snap) => setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (e) => console.warn('doced: templates listener failed', e));
    }, [isNew, orgId]);
    const applyTemplate = (template) => {
        const processed = lineIds.templateLinesFor(template.lineItems, { target: 'quote', newId: lineIds.newLineId, newItemId: generateItemId });
        setQd((prev) => ({
            ...prev,
            title: template.name || prev.title,
            notes: [template.defaultNotes, prev.notes].filter(Boolean).join('\n'),
            lineItems: processed.length > 0 ? processed : prev.lineItems,
            estimatedDuration: template.estimatedDuration || 0,
        }));
        setTemplatesOpen(false);
    };

    // ── catalog ──────────────────────────────────────────────────────────────
    const folders = useCatalogFolders(orgId);
    const [panel, setPanel] = useState({ open: false, query: '' });
    const [addedNote, setAddedNote] = useState('');
    const addPicks = (picks) => {
        // taxpick4-54573069 — Settings › Taxes: each new line starts with its item's code (else the default).
        setQd((prev) => ({ ...prev, lineItems: onCtx ? appendPicksWithTax(prev.lineItems, picks, IDS, onCtx) : appendPickedEstimateLines(prev.lineItems, picks, IDS) }));
        setAddedNote(`Added ${picks.length} ${picks.length === 1 ? 'item' : 'items'}.`);
    };
    const addOneOff = (name) => {
        setQd((prev) => {
            const list = [...prev.lineItems];
            if (list.length && isUntouchedEstimateLine(list[list.length - 1])) list.pop();
            const line = { ...newEstimateLine(IDS), name };
            return { ...prev, lineItems: [...list, onCtx ? withItemSalesTax(line, null, onCtx) : line] }; // taxpick4-54573069 — the default code
        });
        setAddedNote(`Added “${name}”. Set its price in the line.`);
    };

    // ── notes & media ────────────────────────────────────────────────────────
    const [notesOpen, setNotesOpen] = useState(false);
    const [composerDirty, setComposerDirty] = useState(false);
    const noteComposerRef = useRef(null);
    const { notes: quoteNotes } = useOrgNotes({ orgId, attachedKey: quoteId ? `quote:${quoteId}` : null });
    const hasStranded = isNew && !savedOnce && quoteNotes.length > 0;
    const discardStranded = () => {
        if (!hasStranded) return;
        Promise.all(quoteNotes.map((n) => deleteDoc(doc(db, `organizations/${orgId}/notes`, n.id))))
            .catch((e) => console.warn('doced: failed to discard notes on an unsaved estimate', e));
    };
    const guard = useUnsavedGuard({ when: dirty || composerDirty || hasStranded, message: LEAVE_MESSAGE, onLeave: discardStranded });

    // ── save ─────────────────────────────────────────────────────────────────
    // Returns true when the estimate was saved (doced-8f3a61c2 — Review & send saves first and goes on only then).
    const handleSave = async ({ skipDepositConfirm = false, overwrite = false, skipTaxConfirm = false } = {}) => {
        if (savingRef.current) return false;
        const problem = findSaveProblem({ quoteData: qd, customers, serviceLocation, deposit, depositLocked, pendingThirdParty });
        if (problem) { setError(problem); setShowErrors(true); return false; }
        if (taxDocLoading) { setError(TAX_SETTINGS_LOADING); return false; } // taxpick4-54573069 — Taxes on only
        if (!skipDepositConfirm && qd.status === 'Approved' && optionalDep) { setDepositConfirm({ amount: optionalDep.amount }); return false; }
        // taxpick4-54573069 — answer 1: an older estimate a save re-totals says so first; Cancel keeps it as it is.
        if (!skipTaxConfirm && taxNote) { setTaxConfirm({ message: taxNote, overwrite }); return false; }

        setError(''); setConflict(null);
        setSaving(true); savingRef.current = true;
        const seqAtStart = editSeqRef.current;
        const { today, defaultExpiry } = freshDays(companySettings);
        try {
            // "Save to catalog" lines → catalog items first (as the pop-up; a failure keeps the tick).
            const need = linesNeedingCatalogItem(qd.lineItems);
            const created = {};
            await Promise.all(need.map(async (i) => {
                try {
                    const ref = await addDoc(collection(db, `organizations/${orgId}/services`), catalogItemForLine(qd.lineItems[i], serverTimestamp));
                    created[i] = ref.id;
                } catch (e) { console.error('doced: failed to save a line to the catalog', e); }
            }));
            const processed = linesAfterCatalogStep(qd.lineItems, created);
            if (Object.keys(created).length) {
                // Link the lines in the editor too, so a retry never makes the catalog item twice.
                const byItem = new Map(need.filter((i) => created[i]).map((i) => [qd.lineItems[i].itemId, created[i]]));
                setSt((s) => ({ ...s, qd: { ...s.qd, lineItems: s.qd.lineItems.map((l) => (byItem.has(l.itemId) ? { ...l, serviceId: byItem.get(l.itemId), addToCatalog: false } : l)) } }));
            }
            const onSaveTax = onCtx ? onModeTotals(processed, onCtx, source) : null; // taxpick4-54573069 — Taxes on only
            const t = onSaveTax ? onSaveTax.totals : estimateTotals(processed, engineCtx);
            const quoteData = isHighLevel && salesTouched ? { ...qd, salesPersonId } : qd;
            const dataToSave = buildEstimateWrite({
                quoteData, editingQuote: source, sendEmail: false,
                totals: { subtotal: t.subtotal, taxAmount: t.taxAmount, total: t.total },
                processedLineItems: processed,
                deposit: { locked: depositLocked, type: deposit.type, value: deposit.value, enabled: deposit.enabled, required: deposit.required },
                today, defaultExpiry,
            }, { serverTimestamp, parseLocalDate, formatLocalDate, ensureLineIds: lineIds.ensureLineIds, newLineId: lineIds.newLineId });
            const write = isNew ? buildEstimateCreate(dataToSave, {
                docType: resolveDocTerminology(qd.docType ?? companySettings?.quoteTerminologyDefault).type,
                createdBy: currentUserProfile.id,
                salesPersonId: isHighLevel && salesTouched ? salesPersonId : resolveCreateSalesPersonId(selectedCustomer, currentUserProfile.id),
                requestId: source?.requestId,
            }) : dataToSave;
            // taxlines2-3e0b16a4 — Taxes off: the page doesn't store tax parts, so an edit drops the stored ones,
            // exactly as the pop-up does. Never on a create.
            dropStaleTaxParts(write, { isEdit: !isNew, retotals: true, deleteValue: deleteField() });
            // taxpick4-54573069 — Taxes on: store what the engine worked out (as the server would), lines
            // stamped with their code, the exemption stamp; an office role's save clears the field markers.
            if (onSaveTax) applyOnModeSave(write, { result: onSaveTax, ctx: onCtx, isEdit: !isNew, isOfficeRole: isOfficeTaxRole(role), deleteValue: deleteField() });
            const ref = isNew ? newRef : doc(db, `organizations/${orgId}/quotes`, quoteId);

            await saveWithConflictCheck({ runTransaction, db, ref, write, isNew, baseline: baselineRef.current, overwrite });

            // The note staged in Notes & media, after the estimate exists (never fails the save).
            try { await noteComposerRef.current?.commit(); } catch (noteErr) { console.warn('doced: staged note commit failed', noteErr); }

            setSavedOnce(true);
            const clean = editSeqRef.current === seqAtStart;
            if (clean) setDirty(false);
            const before = normalizeStatus(source?.status);
            const after = normalizeStatus(write.status);
            const closing = !isNew && [QUOTE_STATUSES.APPROVED, QUOTE_STATUSES.DECLINED].includes(after) && before !== after;
            if (closing && onClosingSave) onClosingSave(quoteId);

            if (!isNew) {
                // Start again from what is stored now (one read), so the next save compares with it.
                try {
                    const fresh = await getDoc(ref);
                    if (fresh.exists()) {
                        const data = { id: fresh.id, ...fresh.data() };
                        if (clean) reinit(data); else baselineRef.current = fingerprintOf(data);
                    }
                } catch (e) { console.warn('doced: re-read after save failed', e); }
            }
            guard.allow(() => onSaved({ id: quoteId, isNew, status: write.status, data: stripFirestoreSentinels({ id: quoteId, ...write }) }));
            return true;
        } catch (err) {
            if (err instanceof EstimateConflictError) {
                setConflict({ message: err.message, reloadOnly: err.reloadOnly, fields: err.fields });
            } else if (err?.code === 'permission-denied' && approvalBlocked) {
                setError(`The deposit must be paid before this ${docWordLower} can be approved.`);
            } else if (err?.code === 'permission-denied') {
                setError('Spatchy didn’t allow this save. You may not have permission to change some of these details.');
            } else {
                console.error('doced: estimate save failed', err);
                setError(err?.message || 'The estimate couldn’t be saved. Please try again.');
            }
        } finally {
            setSaving(false); savingRef.current = false;
        }
        return false; // only reached after a refused / failed save (success returns above)
    };

    // doced-8f3a61c2 — Stage 5f: Review & send. The email and PDF previews show the SAVED estimate, and Send
    // marks the saved estimate Sent, so unsaved changes (or a brand-new estimate) are saved first; a refused
    // save stays here with its message.
    const reviewAndSend = async () => {
        if (!onReviewAndSend || saving) return;
        // depdraft-3c8e5a14 — stop here, before saving or leaving, while "Collect a deposit" has no amount.
        if (depositAmountMissing(deposit, depositLocked)) { setError(DEPOSIT_AMOUNT_NEEDED_MESSAGE); setShowErrors(true); return; }
        if (isNew || dirty || composerDirty) { const ok = await handleSave(); if (!ok) return; }
        guard.allow(() => onReviewAndSend(quoteId));
    };

    const reloadTheirs = async () => {
        if (dirty && !window.confirm('Your unsaved changes on this page will be lost. Reload?')) return;
        try {
            const fresh = await getDoc(doc(db, `organizations/${orgId}/quotes`, quoteId));
            if (fresh.exists()) reinit({ id: fresh.id, ...fresh.data() });
            else setError('This estimate was deleted by someone else.');
        } catch (e) { setError('Couldn’t reload the estimate. Check your connection and try again.'); }
    };

    // Leaving by the page's own links asks first when something isn't saved (the guard), then goes.
    const leaveTo = (fn) => { if (!guard.confirm()) return; guard.allow(fn); };
    const toList = () => leaveTo(onBackToList);
    const toReadView = () => leaveTo(onBackToReadView);

    // ── render ───────────────────────────────────────────────────────────────
    const number = live?.quoteNumber || source?.quoteNumber;
    const saveState = saving ? 'Saving…' : dirty ? 'Unsaved changes' : (isNew ? 'Not saved yet' : 'All changes saved');
    const statusIsDraft = normalizeStatus(qd.status) === QUOTE_STATUSES.DRAFT;
    const saveLabel = statusIsDraft ? 'Save draft' : 'Save changes';
    const label = 'text-[13px] font-semibold text-gray-700';
    const field = FIELD;
    const card = 'bg-white border border-gray-200 rounded-2xl p-5';

    return (
        <div className="bg-[#f5f6f8] min-h-full">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-5 pt-5 pb-16 flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <nav aria-label="Breadcrumb" className="text-sm text-gray-500 flex flex-wrap items-center gap-1 min-w-0">
                        <button type="button" onClick={toList} className="min-h-[44px] px-1 text-[#4f40b0] hover:underline">{listLabel}</button>
                        <ChevronRight size={14} aria-hidden="true" />
                        <span className="text-gray-900 font-semibold">{number || (isNew ? `New ${docWordLower}` : `${term.word} (number coming)`)}</span>
                    </nav>
                    <span className="text-[13px] text-gray-500" aria-live="polite">{saveState}</span>
                </div>

                {outsideChange && (
                    <div role="alert" className="p-3 rounded-xl bg-amber-50 border border-amber-300 text-sm text-amber-900 flex flex-wrap items-center gap-3">
                        <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
                        <span className="flex-1 min-w-[200px]">Someone else changed this {docWordLower} while you were editing ({describeChanges(outsideChange)}). Your changes aren’t saved yet.</span>
                        <button type="button" onClick={reloadTheirs} className="min-h-[44px] px-3 rounded-lg border border-amber-400 bg-white font-semibold">Reload their changes</button>
                    </div>
                )}

                <div className="flex flex-wrap gap-5 items-start">
                    <main className="flex-[999_1_640px] min-w-0 flex flex-col gap-4">
                        <section aria-label={`${term.word} details`} className={`${card} flex flex-col gap-4`}>
                            <div className="flex flex-col gap-1.5">
                                <label htmlFor="doced-title" className={label}>Title</label>
                                <input id="doced-title" value={qd.title || ''} onChange={(e) => setQd((p) => ({ ...p, title: e.target.value }))}
                                    className="w-full h-[48px] px-3 text-xl font-bold border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40 focus:border-[#5F4EC9]" />
                            </div>
                            <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))]">
                                <DocumentCustomerCard
                                    quoteData={qd} customers={customers} locked={customerLocked} showErrors={showErrors && !qd.customerId}
                                    serviceLocation={serviceLocation} pendingThirdParty={pendingThirdParty}
                                    onChooseCustomer={(id) => patch((s) => { const r = chooseCustomer(s.qd, id, customers); return { qd: r.quoteData, serviceLocation: r.serviceLocation, pendingThirdParty: r.pendingThirdParty, billToTouched: r.billToTouched }; })}
                                    onClear={() => patch((s) => { const r = chooseCustomer(s.qd, '', customers); return { qd: r.quoteData, serviceLocation: r.serviceLocation, pendingThirdParty: r.pendingThirdParty, billToTouched: r.billToTouched }; })}
                                    onServiceLocation={(v) => patch((s) => { const r = chooseServiceLocation(s.qd, v, customers, s.billToTouched); return { qd: r.quoteData, serviceLocation: r.serviceLocation, ...(r.pendingThirdParty === false ? { pendingThirdParty: false } : {}) }; })}
                                    onBillTo={(v) => patch((s) => { const r = chooseBillTo(s.qd, v, customers); return { qd: r.quoteData, pendingThirdParty: r.pendingThirdParty, billToTouched: r.billToTouched }; })}
                                    onThirdParty={(v) => patch((s) => { const r = chooseThirdParty(s.qd, v, customers); return { qd: r.quoteData, pendingThirdParty: r.pendingThirdParty, billToTouched: r.billToTouched }; })}
                                    onNewCustomer={() => setNewCustomerOpen(true)}
                                    // doced-8f3a61c2 — Stage 5f-2: the estimate's own address is an ordinary unsaved edit
                                    // (kept by Save); the customer record, if ticked, is a separate record changed now.
                                    canUpdateRecord={canUpdateCustomerRecord(currentUserProfile, companySettings)}
                                    onChangeEmail={async (email, alsoRecord, recordEmail) => {
                                        const plan = plannedChanges({ email, estimateEmail: estimateEmailOf(qd), recordEmail, offerRecord: true, alsoRecord });
                                        if (plan.estimate) setQd((p) => withEstimateEmail(p, email));
                                        const keep = plan.estimate ? ` Save the ${docWordLower} to keep the new address on it.` : '';
                                        if (!plan.record) return { ok: true, message: keep.trim() };
                                        const r = await updateCustomerRecordEmail({ email }, { customerRef: doc(db, `organizations/${orgId}/customers`, qd.customerId), updateDoc, serverTimestamp });
                                        if (r.record === 'refused' && !plan.estimate) return { ok: false, message: 'The customer record couldn’t be changed. You may not have permission to edit this customer.' };
                                        return { ok: true, message: r.record === 'refused' ? `${r.message}${keep}` : `${RECORD_UPDATED}${keep}` };
                                    }}
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 content-start">
                                    {isNew && (
                                        <div className="flex flex-col gap-1.5 sm:col-span-2">
                                            <label htmlFor="doced-doctype" className={label}>Document type</label>
                                            <select id="doced-doctype" value={qd.docType ?? (companySettings?.quoteTerminologyDefault || DOC_TYPES.QUOTE)}
                                                onChange={(e) => { const v = e.target.value; setQd((p) => ({ ...p, docType: v, title: autoTitle(p.title, v) })); }}
                                                style={SELECT_STYLE} className={SELECT}>
                                                <option value={DOC_TYPES.QUOTE}>Quote</option>
                                                <option value={DOC_TYPES.ESTIMATE}>Estimate</option>
                                            </select>
                                        </div>
                                    )}
                                    <div className="flex flex-col gap-1.5">
                                        <label htmlFor="doced-date" className={label}>{term.word} date</label>
                                        <input id="doced-date" type="date" value={qd.createdAt || ''} onChange={(e) => setQd((p) => ({ ...p, createdAt: e.target.value }))} className={field} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label htmlFor="doced-expiry" className={label}>Expires</label>
                                        <input id="doced-expiry" type="date" value={qd.expiresAt || ''} onChange={(e) => setQd((p) => ({ ...p, expiresAt: e.target.value }))} className={field} />
                                    </div>
                                    <div className="flex flex-col gap-1.5 sm:col-span-2">
                                        <label htmlFor="doced-sales" className={label}>Salesperson</label>
                                        {isHighLevel ? (
                                            <select id="doced-sales" value={salesPersonId || ''} style={SELECT_STYLE} className={SELECT}
                                                onChange={(e) => { const v = e.target.value; patch((s) => ({ salesTouched: true, qd: { ...s.qd, salesPersonId: v } })); }}>
                                                {!salesPersonId && <option value="">Not set</option>}
                                                {salesOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                            </select>
                                        ) : (
                                            <>
                                                <input id="doced-sales" value={salesName(salesPersonId) || (salesPersonId ? 'Someone no longer active' : 'Not set')} disabled className={field} />
                                                <span className="text-xs text-gray-500">Only an Owner, Admin or Manager can change who this {docWordLower} is credited to.</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {onResult && (
                            // taxpick4-54573069 — the customer's exemption on THIS page (decision 7): the S3 banner, fed
                            // what saving will store. Its buttons change the SAVED estimate on the server, so they wait
                            // for a save; afterwards the page starts again from the server's figures.
                            <TaxExemptionBanner kind="quote" orgId={orgId} currentUserProfile={currentUserProfile}
                                document={{ ...(source || {}), id: isNew ? undefined : quoteId, taxAmount: totals.taxAmount, taxParts: onResult.storedFields.taxParts }}
                                blockedReason={isNew ? `Save the ${docWordLower} first to change its exemption.` : (dirty ? 'Save your changes first.' : null)}
                                onFresh={(data) => reinit(data)} />
                        )}
                        <section aria-label="Line items" className={`${card} flex flex-col gap-3`}>
                            <div className="flex flex-wrap justify-between items-center gap-2">
                                <h2 className="text-[17px] font-bold text-gray-900">Line items</h2>
                                <div className="flex flex-wrap gap-2 items-center">
                                    {isNew && templates.length > 0 && (
                                        <button type="button" onClick={() => setTemplatesOpen((o) => !o)} aria-expanded={templatesOpen}
                                            className="min-h-[44px] px-3 rounded-lg text-sm font-semibold text-[#4f40b0] hover:bg-[#5F4EC9]/10">Start from a template</button>
                                    )}
                                    <button type="button" onClick={() => setPanel({ open: true, query: '' })}
                                        className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border border-[#5F4EC9]/30 bg-[#5F4EC9]/10 text-[#3d3194] text-sm font-bold hover:bg-[#5F4EC9]/15">
                                        <ListPlus size={18} aria-hidden="true" />Add from catalog
                                    </button>
                                </div>
                            </div>
                            {templatesOpen && (
                                <div className="border border-gray-200 rounded-xl overflow-hidden">
                                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                                        <span className="text-sm font-semibold text-gray-800">Choose a template</span>
                                        <button type="button" aria-label="Close templates" onClick={() => setTemplatesOpen(false)} className="w-11 h-11 inline-flex items-center justify-center rounded-lg hover:bg-gray-100"><X size={16} /></button>
                                    </div>
                                    <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                                        {templates.map((t) => (
                                            <button key={t.id} type="button" onClick={() => applyTemplate(t)} className="w-full flex items-start gap-3 px-3 min-h-[52px] py-2 text-left hover:bg-gray-50">
                                                <FileText size={16} className="text-[#5F4EC9] mt-0.5 shrink-0" aria-hidden="true" />
                                                <span className="flex-1 min-w-0">
                                                    <span className="block text-sm font-semibold text-gray-800 truncate">{t.name}</span>
                                                    <span className="block text-xs text-gray-500">{t.lineItems?.length ? `${t.lineItems.length} line item${t.lineItems.length > 1 ? 's' : ''} — replaces the lines below` : 'No line items'}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {taxPick && (fieldMarks > 0 || taxPick.canPick) && (
                                // taxpick4-54573069 — "Set tax for all lines" (picker roles) and the field-marker count (office roles).
                                <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                                    {fieldMarks > 0 && (
                                        <span className="mr-auto text-xs font-semibold text-amber-800">{fieldMarks === 1 ? '1 line was' : `${fieldMarks} lines were`} added in the field — check the tax before saving.</span>
                                    )}
                                    {setAllDoneFor && setAllDoneFor === qd.lineItems && <span className="text-xs font-semibold text-green-700" role="status">{SET_ALL_DONE}</span>}
                                    {taxPick.canPick && (
                                        <div className="flex items-center gap-2">
                                            <label htmlFor="doced-tax-all" className="text-[13px] font-semibold text-gray-700">Set tax for all lines</label>
                                            <select id="doced-tax-all" value="__choose" style={SELECT_STYLE} className={`${LINE_SELECT} !w-auto min-w-[170px]`}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v === '__choose') return;
                                                    const next = setAllLines(qd.lineItems, v, onCtx);
                                                    setQd((p) => ({ ...p, lineItems: next }));
                                                    setSetAllDoneFor(next);
                                                }}>
                                                <option value="__choose">Choose…</option>
                                                {setAllChoices(onCtx, taxCtx).map((c) => <option key={c.value || 'default'} value={c.value} title={c.title}>{c.label}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            )}
                            <DocumentLineItems
                                lines={qd.lineItems}
                                onLinesChange={(next) => setQd((p) => ({ ...p, lineItems: next }))}
                                totalsLines={totals.lines}
                                taxCtx={taxCtx}
                                currency={currency}
                                canSaveToCatalog={canUseCatalog(role)}
                                showErrors={showErrors}
                                taxPick={taxPick}
                                footer={(
                                    <div className="flex flex-col gap-1">
                                        <CatalogQuickAdd
                                            onPick={(item) => addPicks([{ item, qty: 1 }])}
                                            onOneOff={addOneOff}
                                            onBrowse={(q) => setPanel({ open: true, query: q })}
                                            pathOf={folders.pathOf}
                                            currency={currency}
                                        />
                                        <span className="text-xs text-gray-500" aria-live="polite">{addedNote}</span>
                                    </div>
                                )}
                            />
                        </section>

                        <section aria-label="Message, terms and internal notes" className={`${card} grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))]`}>
                            <div className="flex flex-col gap-1.5">
                                <label htmlFor="doced-message" className={label}>Message to the customer</label>
                                <textarea id="doced-message" aria-describedby="doced-message-help" rows={4} value={qd.description || ''} onChange={(e) => setQd((p) => ({ ...p, description: e.target.value }))}
                                    placeholder="A short note for the customer"
                                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40" />
                                <span id="doced-message-help" className="text-xs text-gray-500">Shown on the customer portal.</span>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label htmlFor="doced-notes" className={label}>Internal notes</label>
                                <textarea id="doced-notes" aria-describedby="doced-notes-help" rows={4} value={qd.notes || ''} onChange={(e) => setQd((p) => ({ ...p, notes: e.target.value }))}
                                    placeholder="Notes for your team"
                                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40" />
                                <span id="doced-notes-help" className="text-xs text-gray-500">Only your team sees this.</span>
                            </div>
                            {/* doced-8f3a61c2 — Stage 5e: the estimate's own customer-facing Terms (quote.terms). A new
                                estimate starts with the company default (Settings › Documents); an older estimate with
                                none shows an empty box and gains terms only if someone types them. */}
                            <div className="flex flex-col gap-1.5 col-span-full">
                                <label htmlFor="doced-terms" className={label}>Terms</label>
                                <textarea id="doced-terms" aria-describedby="doced-terms-help" rows={6} value={qd.terms || ''} onChange={(e) => { if (!termsLocked) setQd((p) => ({ ...p, terms: e.target.value })); }}
                                    readOnly={termsLocked} placeholder={termsLocked ? '' : 'Your terms for this estimate'}
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40 ${termsLocked ? 'bg-gray-50 text-gray-600' : ''} ${termsProblem(qd.terms || '') ? 'border-red-400' : 'border-gray-300'}`} />
                                <span id="doced-terms-help" className="text-xs text-gray-500">
                                    {termsLocked ? TERMS_LOCKED_NOTE : 'Printed on the PDF and the customer portal.'}
                                    {termsProblem(qd.terms || '') && <span className="text-red-600 font-semibold"> {termsProblem(qd.terms || '')}</span>}
                                </span>
                            </div>
                        </section>

                        {quoteId && (
                            <section aria-label="Notes and media" className={`${card} !py-0`}>
                                <button type="button" onClick={() => setNotesOpen((o) => !o)} aria-expanded={notesOpen}
                                    className="w-full min-h-[56px] flex items-center justify-between gap-2 text-left">
                                    <span className="text-[15px] font-bold text-gray-900">Notes &amp; media <span className="text-gray-500 font-normal">({quoteNotes.length}) · internal unless you mark them customer-facing</span></span>
                                    <ChevronDown size={18} className={`text-gray-400 transition-transform ${notesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                                </button>
                                {notesOpen && (
                                    <div className="pb-5 flex flex-col gap-3">
                                        <NoteComposer
                                            ref={noteComposerRef}
                                            attachedTo={[{ type: 'quote', id: quoteId }]}
                                            orgId={orgId}
                                            currentUserProfile={currentUserProfile}
                                            allowFlagging={false}
                                            onDirtyChange={setComposerDirty}
                                            placeholder={`Add a note or photo to this ${docWordLower}...`}
                                        />
                                        <div className="flex flex-col gap-2">
                                            {quoteNotes.length === 0
                                                ? <p className="text-sm text-gray-500 text-center py-4">No notes yet — add the first one above.</p>
                                                : quoteNotes.map((note) => <NoteCard key={note.id} note={note} currentUserProfile={currentUserProfile} orgId={orgId} />)}
                                        </div>
                                    </div>
                                )}
                            </section>
                        )}
                    </main>

                    <aside aria-label="Summary and actions" className="flex-[1_1_300px] w-full lg:max-w-[380px] min-w-0 sm:min-w-[280px] lg:sticky lg:top-4 flex flex-col gap-3">
                        <DocumentTotalsPanel
                            status={qd.status}
                            onStatus={(v) => setQd((p) => ({ ...p, status: v }))}
                            approvalBlocked={approvalBlocked}
                            optionalDepositNote={qd.status === 'Approved' && optionalDep
                                ? `Approving marks this ${docWordLower}’s ${formatCurrency(optionalDep.amount, currency)} deposit as owing. You can record a payment or waive it afterward.`
                                : (normalizeStatus(qd.status) === QUOTE_STATUSES.SENT && normalizeStatus(source?.status) !== QUOTE_STATUSES.SENT
                                    ? `This only marks the ${docWordLower} as sent — it doesn’t email the customer.`
                                    : null)}
                            totals={totals}
                            taxProblem={taxProblem}
                            onRetryTax={tax.error ? () => setTaxNonce((n) => n + 1) : null}
                            currency={currency}
                            docWordLower={docWordLower}
                            deposit={deposit}
                            onDeposit={(d) => patch({ deposit: d })}
                            depositLocked={depositLocked}
                            lockedDeposit={{ amountResolved: qd.deposit?.amountResolved, depositStatus: qd.depositStatus }}
                            depositPreview={depositPreview}
                            stripeReady={canOrgTakeOnlinePayments(companySettings)}
                            saving={saving}
                            onSave={() => handleSave()}
                            saveLabel={saveLabel}
                            error={error}
                            conflict={conflict}
                            onReload={reloadTheirs}
                            onOverwrite={() => handleSave({ overwrite: true, skipDepositConfirm: true })}
                            rowLabel={onCtx ? onModeRowLabel : undefined}
                            taxChangeNote={taxNote}
                        />
                        {/* doced-8f3a61c2 — Stage 5g: Owner / Admin / Manager / Office Staff (offcost-2e7c9a14) only —
                            canUseCatalog = the server's COST_ROLES. Not mounted at all for anyone else, so their browser
                            never asks for costs (hard rule, 2026-10-30). On screen only, never saved. */}
                        {canUseCatalog(role) && (
                            <DocumentProfitCard role={role} lines={qd.lineItems} totalsLines={totals.lines} currency={currency} />
                        )}
                        {/* doced-8f3a61c2 — Stage 5f: not offered where sending would undo an approval / decline. */}
                        {onReviewAndSend && statusAllowsSend(qd.status) && !original?.convertedToJobId && (
                            <button type="button" onClick={reviewAndSend} disabled={saving}
                                className="min-h-[46px] rounded-lg border-2 border-[#5F4EC9] bg-white text-[15px] font-bold text-[#5F4EC9] hover:bg-[#5F4EC9]/5 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                                <Send size={16} aria-hidden="true" />{isNew || dirty ? 'Save, then review & send' : 'Review & send'}
                            </button>
                        )}
                        {!isNew && !saving && normalizeStatus(source?.status) !== QUOTE_STATUSES.DRAFT && onBackToReadView && (
                            <button type="button" onClick={toReadView} className="min-h-[44px] rounded-lg text-sm font-semibold text-gray-600 hover:bg-white">{dirty ? 'Stop editing (asks before discarding changes)' : `Back to the ${docWordLower}`}</button>
                        )}
                        {/* estdel-f9ac8ba8 — a Draft opens straight into this editor (no read view), so Delete is offered here too:
                            the saved estimate, only with nothing unsaved (deleting must never throw away someone's typing). */}
                        {!isNew && !saving && !dirty && !composerDirty && (live || original) && (
                            <DeleteQuoteButton quote={live || original} currentUserProfile={currentUserProfile} className="min-h-[44px]" />
                        )}
                    </aside>
                </div>
            </div>

            <CatalogPickerPanel
                open={panel.open}
                initialQuery={panel.query}
                onClose={() => setPanel({ open: false, query: '' })}
                onAdd={(picks) => { addPicks(picks); setPanel({ open: false, query: '' }); }}
                folders={folders}
                currency={currency}
            />

            {taxConfirm && (
                <ConfirmationModal
                    isOpen={true}
                    onClose={() => setTaxConfirm(null)}
                    onConfirm={() => { const c = taxConfirm; setTaxConfirm(null); handleSave({ skipTaxConfirm: true, overwrite: c.overwrite, skipDepositConfirm: true }); }}
                    title="Change the tax on this estimate?"
                    message={taxConfirm.message}
                    confirmText="Save"
                />
            )}
            {depositConfirm && (
                <ConfirmationModal
                    isOpen={true}
                    onClose={() => setDepositConfirm(null)}
                    onConfirm={() => { setDepositConfirm(null); handleSave({ skipDepositConfirm: true }); }}
                    title="Approve and mark the deposit as owing?"
                    message={`This ${docWordLower} has a ${formatCurrency(depositConfirm.amount, currency)} deposit. Approving marks it as owing — you can record a payment or waive it afterward.`}
                    confirmText="Approve & mark owing"
                />
            )}

            {newCustomerOpen && (
                <CustomerFormModal
                    closeModal={() => setNewCustomerOpen(false)}
                    editingCustomer={null}
                    user={user}
                    currentUserProfile={currentUserProfile}
                    customers={customers || []}
                    initialTab="contact"
                    companySettings={companySettings}
                    onCreated={(c) => patch((s) => { const r = chooseCustomer(s.qd, c.id, [...(customers || []), c]); return { qd: r.quoteData, serviceLocation: r.serviceLocation, pendingThirdParty: r.pendingThirdParty, billToTouched: r.billToTouched }; })}
                />
            )}
        </div>
    );
}

